import type { Issue } from '../types/issue';

// Feld heißt bei euch „Custom field (Expected Behaviour)" (britisch mit u).
export const EB_FIELD = 'Custom field (Expected Behaviour)';

export function expectedBehaviourText(row: Issue): string {
  const v = (row as Record<string, unknown>)[EB_FIELD];
  if (v == null || v === '') return '';
  const val = Array.isArray(v) ? (v[0] ?? '') : v;
  return String(val).trim();
}

// ─── Sprach-Heuristik ─────────────────────────────────────────────────────

// Zählt Vorkommen typischer Marker-Wörter. Nicht wissenschaftlich, aber
// robust für kurze bis mittlere Bug-Beschreibungen.
const DE_MARKERS = /\b(der|die|das|und|ist|sind|nicht|mit|von|dem|den|ein|eine|einen|für|beim|nach|dass|sollte|wäre|kann|soll|muss|müssen|werden|wird|wurde|hat|haben|zum|zur|auf|auch|über|unter|durch|beim|beim|kein|keine|noch|schon|hier|dort|geändert|erwartet|verhalten)\b/gi;
const EN_MARKERS = /\b(the|and|is|are|not|with|from|for|should|would|could|can|must|will|be|been|when|that|this|these|those|of|to|in|on|at|as|but|by|it|its|expected|behaviour|behavior)\b/gi;

export type Lang = 'de' | 'en' | 'mixed' | 'unknown';

export function detectLang(text: string): Lang {
  if (!text) return 'unknown';
  const de = (text.match(DE_MARKERS) ?? []).length;
  const en = (text.match(EN_MARKERS) ?? []).length;
  if (de === 0 && en === 0) return 'unknown';
  // Signifikant deutsch, wenn DE ≥ 2× EN
  if (de >= en * 2)   return 'de';
  if (en >= de * 2)   return 'en';
  return 'mixed';
}

// ─── Qualitäts-Buckets ────────────────────────────────────────────────────

export type QualityBucket = 'empty' | 'stub' | 'short' | 'ok' | 'long' | 'huge';

// Schwellwerte: Erfahrungswerte für Bug-Beschreibungen.
export function qualityBucket(text: string): QualityBucket {
  const len = text.trim().length;
  if (len === 0) return 'empty';
  if (len < 20)  return 'stub';   // "s.o.", "Preis ermitteln"
  if (len < 60)  return 'short';  // Ein-Satz-Beschreibung
  if (len < 500) return 'ok';     // Vernünftig ausformuliert
  if (len < 2000) return 'long';  // Sehr ausführlich, oft OK
  return 'huge';                  // > 2 KB — meist zu ausufernd oder Copy-Paste
}

export const QUALITY_LABELS: Record<QualityBucket, string> = {
  empty: 'leer',
  stub:  'zu kurz (<20 Z.)',
  short: 'kurz (20–60 Z.)',
  ok:    'gut (60–500 Z.)',
  long:  'lang (500–2000 Z.)',
  huge:  'überlang (>2 KB)',
};

export const QUALITY_COLORS: Record<QualityBucket, string> = {
  empty: '#9e9e9e',
  stub:  '#c62828',
  short: '#ef6c00',
  ok:    '#2e7d32',
  long:  '#1976d2',
  huge:  '#7b1fa2',
};

// ─── Aggregations ────────────────────────────────────────────────────────

export type EBStats = {
  total:        number;
  withEB:       number;
  withoutEB:    number;
  avgLen:       number;
  medianLen:    number;
  langCounts:   Record<Lang, number>;         // nur Tickets mit EB
  qualityCounts:Record<QualityBucket, number>;// alle (empty = ohne EB)
  perType:      { type: string; total: number; withEB: number }[];
  perTeam:      { team: string; total: number; withEB: number }[];
};

export function computeEBStats(rows: Issue[]): EBStats {
  const langCounts:    Record<Lang, number>          = { de: 0, en: 0, mixed: 0, unknown: 0 };
  const qualityCounts: Record<QualityBucket, number> = { empty: 0, stub: 0, short: 0, ok: 0, long: 0, huge: 0 };
  const perTypeMap = new Map<string, { total: number; withEB: number }>();
  const perTeamMap = new Map<string, { total: number; withEB: number }>();
  const lens: number[] = [];
  let total = 0, withEB = 0, sumLen = 0;

  for (const r of rows) {
    total++;
    const t = String(r['Issue Type'] ?? '?');
    const team = String(r['Team Name'] ?? '(kein Team)');
    let pt = perTypeMap.get(t);
    if (!pt) { pt = { total: 0, withEB: 0 }; perTypeMap.set(t, pt); }
    let pm = perTeamMap.get(team);
    if (!pm) { pm = { total: 0, withEB: 0 }; perTeamMap.set(team, pm); }
    pt.total++;
    pm.total++;

    const eb = expectedBehaviourText(r);
    const bucket = qualityBucket(eb);
    qualityCounts[bucket]++;
    if (eb) {
      withEB++;
      pt.withEB++;
      pm.withEB++;
      sumLen += eb.length;
      lens.push(eb.length);
      langCounts[detectLang(eb)]++;
    }
  }
  lens.sort((a, b) => a - b);
  const avgLen    = withEB > 0 ? Math.round(sumLen / withEB) : 0;
  const medianLen = lens.length ? lens[Math.floor(lens.length / 2)] : 0;

  return {
    total, withEB, withoutEB: total - withEB,
    avgLen, medianLen,
    langCounts,
    qualityCounts,
    perType: Array.from(perTypeMap.entries())
      .map(([type, v]) => ({ type, total: v.total, withEB: v.withEB }))
      .sort((a, b) => b.total - a.total),
    perTeam: Array.from(perTeamMap.entries())
      .map(([team, v]) => ({ team, total: v.total, withEB: v.withEB }))
      .sort((a, b) => b.total - a.total),
  };
}
